import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  CheckCircleIcon,
  ExclamationTriangleIcon,
  EyeSlashIcon,
  SparklesIcon,
  ArrowLeftIcon,
  ArrowRightIcon,
} from "@heroicons/react/24/solid";
import useApiToken from "../../../hooks/useApiToken";
import { useNotification } from "../../../context/NotificationContext";
import { Spinner } from "../../Shared/Spinner";
import CompetencyScoreCard, { CompetencyDraft } from "./CompetencyScoreCard";
import StarRatingDisplay from "./StarRatingDisplay";
import ScoreFormulaInfo from "./ScoreFormulaInfo";
import EvaluationStepper, { StepDefinition } from "./EvaluationStepper";
import {
  Competency,
  DraftFromTranscript,
  EvaluationValidationError,
  GetCompetencies,
  GetMyEvaluation,
  GetStageEvaluations,
  InterviewEvaluation,
  RECOMMENDATION_LABELS,
  RECOMMENDATION_STYLES,
  SaveEvaluationDraft,
  SaveEvaluationRequest,
  StageEvaluationSummary,
  SubmitEvaluation,
  suggestLevel,
} from "../../../api/interviewEvaluation.api";

type Props = {
  candidateId: string;
  roleId: string;
  roleStatusId: string;
  stageName?: string;
  candidateName?: string;
  /**
   * Fired right after a successful submit. Submitting here also updates the legacy
   * CandidateManualEvaluation row via the server-side projection, so a caller embedding
   * this in a modal over the candidate page (see RequiredScorecardModal) can use this to
   * refresh the grade/badge behind it without the user reloading the page.
   */
  onSubmitted?: () => void;
};

const emptyDraft = (): CompetencyDraft => ({
  level: null,
  notObserved: false,
  notObservedReason: "",
  deviationReason: "",
  confidence: "Medium",
  evidenceText: "",
  signalIds: [],
});

const isDraftComplete = (d: CompetencyDraft | undefined): boolean => {
  if (!d) return false;
  return d.notObserved ? d.notObservedReason.trim().length > 0 : d.level !== null;
};

export default function InterviewScorecard({
  candidateId,
  roleId,
  roleStatusId,
  stageName,
  candidateName,
  onSubmitted,
}: Props) {
  const token = useApiToken();
  const { success, error: notifyError } = useNotification();

  const [loading, setLoading] = useState(true);
  const [featureEnabled, setFeatureEnabled] = useState(true);
  const [competencies, setCompetencies] = useState<Competency[]>([]);
  const [drafts, setDrafts] = useState<Record<string, CompetencyDraft>>({});
  const [overallComments, setOverallComments] = useState("");
  const [mine, setMine] = useState<InterviewEvaluation | null>(null);
  const [stage, setStage] = useState<StageEvaluationSummary | null>(null);
  const [problems, setProblems] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [lastSavedAt, setLastSavedAt] = useState<Date | null>(null);

  // Wizard navigation. Step 0 is the transcript autofill step; steps 1..N map to
  // competencies[0..N-1].
  const [currentStep, setCurrentStep] = useState(0);
  const [maxReachedStep, setMaxReachedStep] = useState(0);
  const [stepVisible, setStepVisible] = useState(true);

  // Transcript import - advisory autofill, never auto-submitted.
  const [transcriptText, setTranscriptText] = useState("");
  const [importingTranscript, setImportingTranscript] = useState(false);
  const [transcriptError, setTranscriptError] = useState<string | null>(null);
  const [aiDraftedIds, setAiDraftedIds] = useState<Set<string>>(new Set());

  const submitted = mine?.status === "Submitted";
  const dirtyRef = useRef(false);

  // ── Load ────────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!token || !candidateId || !roleId || !roleStatusId) return;

    let cancelled = false;

    (async () => {
      setLoading(true);
      try {
        const framework = await GetCompetencies(token);

        if (cancelled) return;

        if (framework === null) {
          setFeatureEnabled(false);
          return;
        }

        setCompetencies(framework);

        const [existing, stageSummary] = await Promise.all([
          GetMyEvaluation(candidateId, roleId, roleStatusId, token),
          GetStageEvaluations(candidateId, roleId, roleStatusId, token),
        ]);

        if (cancelled) return;

        setMine(existing);
        setStage(stageSummary);

        const seeded: Record<string, CompetencyDraft> = {};
        framework.forEach((c) => {
          const saved = existing?.competencyScores.find((s) => s.competencyId === c.id);
          seeded[c.id] = saved
            ? {
                level: saved.level,
                notObserved: saved.level === null && !!saved.notObservedReason,
                notObservedReason: saved.notObservedReason ?? "",
                deviationReason: saved.deviationReason ?? "",
                confidence: saved.confidence,
                evidenceText: saved.evidenceText ?? "",
                signalIds: saved.signalIds ?? [],
              }
            : emptyDraft();
        });

        setDrafts(seeded);
        setOverallComments(existing?.overallComments ?? "");

        // Resume where they left off: a brand-new evaluation opens on the transcript
        // step; a partially-filled one skips straight to the first incomplete
        // competency (or the last step, to review, if everything's already scored) so
        // returning to an in-progress evaluation doesn't re-prompt for a transcript
        // every time.
        let startStep = 0;
        if (existing !== null) {
          const firstIncomplete = framework.findIndex(
            (c) => !isDraftComplete(seeded[c.id])
          );
          startStep = firstIncomplete === -1 ? framework.length : firstIncomplete + 1;
        }
        setCurrentStep(startStep);
        setMaxReachedStep(startStep);
      } catch (e) {
        if (!cancelled) {
          notifyError("Could not load scorecard", (e as Error).message);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
    // notifyError is stable enough in practice; excluded to avoid a reload loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, candidateId, roleId, roleStatusId]);

  // Subtle fade+slide whenever the visible step changes.
  useEffect(() => {
    setStepVisible(false);
    const t = setTimeout(() => setStepVisible(true), 20);
    return () => clearTimeout(t);
  }, [currentStep]);

  // ── Build request ───────────────────────────────────────────────────────
  const buildRequest = useCallback((): SaveEvaluationRequest => {
    return {
      candidateId,
      roleId,
      roleStatusId,
      overallComments: overallComments || null,
      competencyScores: competencies.map((c) => {
        const d = drafts[c.id] ?? emptyDraft();
        const positives = c.signals.filter((s) => s.polarity === "Positive");
        const selected = new Set(d.signalIds);

        return {
          competencyId: c.id,
          level: d.notObserved ? null : d.level,
          notObservedReason: d.notObserved ? d.notObservedReason || null : null,
          suggestedLevel: suggestLevel(
            positives.filter((s) => selected.has(s.id)).length,
            c.signals.filter((s) => s.polarity === "Concern" && selected.has(s.id)).length,
            positives.length
          ),
          deviationReason: d.deviationReason || null,
          confidence: d.confidence,
          evidenceText: d.evidenceText || null,
          signalIds: d.signalIds,
        };
      }),
    };
  }, [candidateId, roleId, roleStatusId, overallComments, competencies, drafts]);

  // ── Autosave draft ──────────────────────────────────────────────────────
  // Runs whether or not this evaluation has already been submitted - an interviewer
  // revising a grade they already gave is still editing a form, and losing that work
  // to a stray navigation would be exactly the failure autosave exists to prevent.
  // Saving a draft never re-scores or re-projects the legacy grade; only an explicit
  // Submit/Update click does that.
  useEffect(() => {
    if (!token || loading || competencies.length === 0) return;
    if (!dirtyRef.current) return;

    const handle = setTimeout(async () => {
      setSaving(true);
      try {
        await SaveEvaluationDraft(buildRequest(), token);
        setLastSavedAt(new Date());
        dirtyRef.current = false;
      } catch {
        // A failed autosave is not worth interrupting an interview for. The
        // interviewer still has everything on screen and Submit reports properly.
      } finally {
        setSaving(false);
      }
    }, 1500);

    return () => clearTimeout(handle);
  }, [drafts, overallComments, token, loading, competencies.length, buildRequest]);

  const updateDraft = (competencyId: string, next: CompetencyDraft) => {
    dirtyRef.current = true;
    setDrafts((prev) => ({ ...prev, [competencyId]: next }));

    // Any manual touch to a card retires its "AI-drafted" badge - once the interviewer
    // has looked at it and changed something, it is their evidence, not the model's.
    setAiDraftedIds((prev) => {
      if (!prev.has(competencyId)) return prev;
      const next = new Set(prev);
      next.delete(competencyId);
      return next;
    });
  };

  // ── Wizard navigation ───────────────────────────────────────────────────
  const totalSteps = competencies.length + 1; // transcript + one per competency
  const isCompetencyStep = currentStep >= 1;
  const activeCompetency = isCompetencyStep ? competencies[currentStep - 1] : null;
  const isLastStep = currentStep === competencies.length;

  const currentStepValid = isCompetencyStep
    ? isDraftComplete(activeCompetency ? drafts[activeCompetency.id] : undefined)
    : true;

  const advanceTo = (index: number) => {
    setCurrentStep(index);
    setMaxReachedStep((m) => Math.max(m, index));
  };

  const goNext = () => {
    if (!currentStepValid) return;
    if (isLastStep) {
      handleSubmit();
      return;
    }
    advanceTo(currentStep + 1);
  };

  const goBack = () => setCurrentStep((s) => Math.max(0, s - 1));

  const steps: StepDefinition[] = useMemo(
    () => [
      { key: "transcript", label: "Transcript" },
      ...competencies.map((c) => ({ key: c.id, label: c.name })),
    ],
    [competencies]
  );

  const isStepComplete = (index: number): boolean => {
    if (index === 0) return currentStep > 0 || submitted;
    const c = competencies[index - 1];
    return isDraftComplete(drafts[c.id]);
  };

  // ── Transcript import ───────────────────────────────────────────────────
  const handleImportTranscript = async () => {
    if (!token) return;

    setTranscriptError(null);
    setImportingTranscript(true);

    try {
      const result = await DraftFromTranscript(
        { candidateId, roleId, roleStatusId, transcriptText },
        token
      );

      if (result === null) {
        setTranscriptError("Transcript auto-fill is not enabled for this environment.");
        return;
      }

      const drafted = new Set<string>();

      setDrafts((prev) => {
        const next = { ...prev };

        result.competencies.forEach((c) => {
          const hasContent = c.evidenceText.trim().length > 0 || c.signalIds.length > 0;
          if (!hasContent) return; // nothing found for this competency - leave it as-is

          drafted.add(c.competencyId);
          next[c.competencyId] = {
            level: c.suggestedLevel,
            notObserved: false,
            notObservedReason: "",
            deviationReason: "",
            confidence: c.confidence,
            evidenceText: c.evidenceText,
            signalIds: c.signalIds,
          };
        });

        return next;
      });

      dirtyRef.current = true;
      setAiDraftedIds(drafted);
      setTranscriptText("");

      if (drafted.size === 0) {
        notifyError(
          "Nothing to draft",
          "The transcript did not contain clear evidence for any competency. You can still score each one manually."
        );
      } else {
        success(
          "Draft applied",
          `Drafted ${drafted.size} of ${competencies.length} competencies. Review and adjust each score as you go.`
        );
      }

      advanceTo(1);
    } catch (e) {
      setTranscriptError((e as Error).message);
    } finally {
      setImportingTranscript(false);
    }
  };

  const handleSkipTranscript = () => advanceTo(1);

  // ── Progress (for the header count) ────────────────────────────────────
  const progress = useMemo(() => {
    const total = competencies.length;
    const done = competencies.filter((c) => isDraftComplete(drafts[c.id])).length;
    return { done, total };
  }, [competencies, drafts]);

  const handleSubmit = async () => {
    if (!token) return;
    const wasAlreadySubmitted = submitted;
    setProblems([]);
    setSubmitting(true);

    try {
      const saved = await SubmitEvaluation(buildRequest(), token);
      setMine(saved);
      success(
        wasAlreadySubmitted ? "Evaluation updated" : "Evaluation submitted",
        wasAlreadySubmitted
          ? "Your changes are saved."
          : "Other interviewers' evaluations are now visible."
      );

      const refreshed = await GetStageEvaluations(candidateId, roleId, roleStatusId, token);
      setStage(refreshed);

      // Submitting also updated CandidateManualEvaluation server-side (see
      // ManualEvaluationProjectionService) - let the caller refresh whatever grade
      // display is sitting behind this component, live, with no page reload.
      onSubmitted?.();
    } catch (e) {
      if (e instanceof EvaluationValidationError) {
        setProblems(e.problems);
        notifyError("Evaluation is incomplete", "Resolve the highlighted items below.");
      } else {
        notifyError("Could not submit", (e as Error).message);
      }
    } finally {
      setSubmitting(false);
    }
  };

  // ── Render ──────────────────────────────────────────────────────────────

  if (loading) {
    return (
      <div className="py-10">
        <Spinner height="h-8" width="w-8" />
      </div>
    );
  }

  if (!featureEnabled) {
    return (
      <div className="rounded-lg border border-dashed border-gray-300 dark:border-gray-700 p-6 text-sm text-gray-500 dark:text-gray-400">
        Structured interview evaluation is not enabled for this environment.
      </div>
    );
  }

  const others = (stage?.evaluations ?? []).filter((e) => e.id !== mine?.id);

  return (
    <div>
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div>
          <h2 className="text-xl font-semibold text-gray-800 dark:text-gray-200">
            Interview Scorecard
          </h2>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            {candidateName ? `${candidateName} · ` : ""}
            {stageName ?? "Interview stage"}
          </p>
        </div>

        <div className="flex items-center gap-3 text-sm">
          {submitted && (
            <span className="flex items-center gap-1.5 text-green-600 dark:text-green-400">
              <CheckCircleIcon className="h-4 w-4" />
              Submitted
            </span>
          )}
          <span className="text-gray-500 dark:text-gray-400">
            {progress.done}/{progress.total} scored
          </span>
          {saving ? (
            <span className="text-gray-400">Saving…</span>
          ) : lastSavedAt ? (
            <span className="text-gray-400">
              Draft saved {lastSavedAt.toLocaleTimeString()}
            </span>
          ) : null}
        </div>
      </div>
  

      {problems.length > 0 && (
        <div className="mb-5 rounded-md border border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-900/20 p-4">
          <p className="text-sm font-semibold text-amber-800 dark:text-amber-300 mb-2">
            Resolve these before submitting
          </p>
          <ul className="list-disc list-inside space-y-1 text-sm text-amber-800 dark:text-amber-300">
            {problems.map((p, i) => (
              <li key={i}>{p}</li>
            ))}
          </ul>
        </div>
      )}

      {/* Recommendation banner - shown whenever there's a submitted result to report,
          regardless of whether you're now revising it. */}
      {mine?.recommendation && (
        <div className="mb-5 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
          <div className="flex flex-wrap items-center gap-3">
            <span className="text-sm text-gray-500 dark:text-gray-400">
              Your recommendation
            </span>
            <span
              className={`px-3 py-1 rounded-full text-sm font-semibold ${
                RECOMMENDATION_STYLES[mine.recommendation]
              }`}
            >
              {RECOMMENDATION_LABELS[mine.recommendation]}
            </span>
            <ScoreFormulaInfo
              showCombinedRule={!!stage && stage.submittedCount > 1}
              competencies={competencies}
            />
            <span className="text-sm text-gray-500 dark:text-gray-400">
              {mine.normalisedScore}/100 · weighted {mine.weightedScore}
            </span>
          </div>

          {mine.gateBreached && (
            <p className="mt-2 flex items-center gap-1.5 text-xs text-orange-600 dark:text-orange-400">
              <ExclamationTriangleIcon className="h-4 w-4" />
              A competency scored below its minimum, so the recommendation is capped.
            </p>
          )}

          {stage && stage.submittedCount > 1 && stage.combinedRecommendation && (
            <div className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-700 flex flex-wrap items-center gap-3">
              <span className="text-sm text-gray-500 dark:text-gray-400">
                Combined across {stage.submittedCount} interviewers
              </span>
              <span
                className={`px-3 py-1 rounded-full text-sm font-semibold ${
                  RECOMMENDATION_STYLES[stage.combinedRecommendation]
                }`}
              >
                {RECOMMENDATION_LABELS[stage.combinedRecommendation]}
              </span>
            </div>
          )}

        </div>
      )}

      {/* The wizard - always editable and pre-filled from whatever's saved, whether
          that's an in-progress draft or a previously submitted evaluation. Changing a
          grade means revising this, not starting over or being locked out of it. */}
      <EvaluationStepper
        steps={steps}
        currentStep={currentStep}
        maxReachedStep={maxReachedStep}
        isStepComplete={isStepComplete}
        onStepClick={(i) => i <= maxReachedStep && setCurrentStep(i)}
      />

      <div
        className={`transition-all duration-300 ease-out ${
          stepVisible ? "opacity-100 translate-x-0" : "opacity-0 translate-x-3"
        }`}
      >
        {currentStep === 0 ? (
          <div className="rounded-xl border-2 border-dashed border-indigo-200 dark:border-indigo-900/50 bg-indigo-50/40 dark:bg-indigo-900/10 p-8 text-center">
            <SparklesIcon className="h-8 w-8 text-indigo-500 mx-auto mb-3" />
            <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-200 mb-1">
              Auto-fill from a transcript?
            </h3>
            <p className="text-sm text-gray-500 dark:text-gray-400 max-w-md mx-auto mb-5">
              Optional. Paste a transcript of the interview (from Teams, Zoom,
              Otter.ai, or typed up by hand) and it will draft evidence and a
              suggested score for each competency - you review and adjust everything
              before it counts. Or skip and score from memory.
            </p>

            <textarea
              rows={7}
              value={transcriptText}
              onChange={(e) => setTranscriptText(e.target.value)}
              placeholder="Paste the transcript here…"
              className="w-full max-w-xl mx-auto block rounded-md border-gray-300 dark:border-gray-600 dark:bg-slate-800 dark:text-gray-200 text-sm focus:border-indigo-500 focus:ring-indigo-500"
            />
            {transcriptError && (
              <p className="mt-2 text-xs text-red-600 dark:text-red-400">
                {transcriptError}
              </p>
            )}

            <div className="mt-5 flex flex-wrap items-center justify-center gap-3">
              <button
                type="button"
                onClick={handleSkipTranscript}
                className="rounded-md border border-gray-300 dark:border-gray-600 px-4 py-2 text-sm font-medium text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-slate-800"
              >
                Skip &mdash; score manually
              </button>
              <button
                type="button"
                onClick={handleImportTranscript}
                disabled={importingTranscript || transcriptText.trim().length < 200}
                className="inline-flex items-center gap-2 rounded-md bg-indigo-600 px-4 py-2 text-sm font-semibold text-white hover:bg-indigo-700 disabled:opacity-50"
              >
                {importingTranscript && <Spinner height="h-4" width="w-4" />}
                Generate draft &amp; continue
              </button>
            </div>
            {transcriptText.trim().length > 0 && transcriptText.trim().length < 200 && (
              <p className="mt-2 text-xs text-gray-400">
                Paste at least a few exchanges ({transcriptText.trim().length}/200
                characters).
              </p>
            )}
          </div>
        ) : (
          activeCompetency && (
            // key forces a fresh mount per competency - without it, React would
            // reuse the same instance across steps, leaking hover state and
            // breaking the evidence Disclosure's defaultOpen (which only
            // evaluates once, on mount).
            <CompetencyScoreCard
              key={activeCompetency.id}
              competency={activeCompetency}
              draft={drafts[activeCompetency.id] ?? emptyDraft()}
              readOnly={false}
              aiDrafted={aiDraftedIds.has(activeCompetency.id)}
              onChange={(next) => updateDraft(activeCompetency.id, next)}
            />
          )
        )}
      </div>

      {isLastStep && (
        <div className="mt-5">
          <label className="block text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-1">
            Overall comments (optional)
          </label>
          <textarea
            rows={2}
            value={overallComments}
            onChange={(e) => {
              dirtyRef.current = true;
              setOverallComments(e.target.value);
            }}
            placeholder="Anything the per-competency evidence above does not already capture."
            className="w-full rounded-md border-gray-300 dark:border-gray-600 dark:bg-slate-800 dark:text-gray-200 text-sm focus:border-entntblue focus:ring-entntblue"
          />
        </div>
      )}

      {/* Wizard navigation */}
      {currentStep > 0 && (
        <div className="mt-6 flex items-center justify-between">
          <button
            type="button"
            onClick={goBack}
            className="inline-flex items-center gap-1.5 rounded-md px-4 py-2 text-sm font-medium text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-slate-800"
          >
            <ArrowLeftIcon className="h-4 w-4" />
            Back
          </button>

          <div className="flex flex-col items-end gap-1">
            {!currentStepValid && (
              <span className="text-xs text-amber-600 dark:text-amber-400">
                Choose a score (or mark not observed) to continue
              </span>
            )}
            <button
              type="button"
              onClick={goNext}
              disabled={!currentStepValid || submitting}
              className="inline-flex items-center gap-2 rounded-md bg-entntblue px-5 py-2.5 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              {isLastStep ? (
                <>
                  {submitting ? (
                    <Spinner height="h-4" width="w-4" />
                  ) : (
                    <CheckCircleIcon className="h-4 w-4" />
                  )}
                  {submitted ? "Update evaluation" : "Submit evaluation"}
                </>
              ) : (
                <>
                  Next
                  <ArrowRightIcon className="h-4 w-4" />
                </>
              )}
            </button>
          </div>
        </div>
      )}

      {/* Other interviewers - revealed once you've submitted at least once; stays
          visible while you go back and revise your own. */}
      {submitted && others.length > 0 && (
        <div className="mt-8">
          <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-200 mb-3">
            Other interviewers
          </h3>
          {others.map((evaluation) => (
            <div
              key={evaluation.id}
              className="mb-4 rounded-lg border border-gray-200 dark:border-gray-700 p-4"
            >
              <div className="flex flex-wrap items-center gap-3 mb-3">
                <span className="font-medium text-gray-800 dark:text-gray-200">
                  {evaluation.evaluatorName ?? evaluation.evaluatorId}
                </span>
                {evaluation.recommendation && (
                  <span
                    className={`px-2.5 py-0.5 rounded-full text-xs font-semibold ${
                      RECOMMENDATION_STYLES[evaluation.recommendation]
                    }`}
                  >
                    {RECOMMENDATION_LABELS[evaluation.recommendation]}
                  </span>
                )}
                <span className="text-xs text-gray-500 dark:text-gray-400">
                  {evaluation.normalisedScore}/100
                </span>
              </div>

              <div className="space-y-2">
                {evaluation.competencyScores.map((s) => (
                  <div
                    key={s.competencyId}
                    className="rounded-lg border border-gray-100 dark:border-gray-700 bg-gray-50/60 dark:bg-slate-800/40 p-3"
                  >
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <span className="text-sm font-medium text-gray-700 dark:text-gray-200">
                        {s.competencyName}
                      </span>
                      <div className="flex items-center gap-2">
                        <StarRatingDisplay level={s.level} />
                        {s.level !== null && (
                          <span className="text-[11px] text-gray-400 dark:text-gray-500">
                            {s.confidence} confidence
                          </span>
                        )}
                      </div>
                    </div>
                    {s.evidenceText && (
                      <p className="mt-1.5 text-xs text-gray-500 dark:text-gray-400">
                        {s.evidenceText}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
